/**
 * Docs:
 * https://developers.google.com/recaptcha/docs/v3
 * https://developers.google.com/recaptcha/docs/loading#lazy_loading
 */

import type { ReCaptchaInstance } from 'recaptcha-v3'
import { load } from 'recaptcha-v3'

import type { RecaptchaAction } from '@/types/internal'

const GOOGLE_RECAPTCHA_SITE_KEY = import.meta.env.VITE_GOOGLE_RECAPTCHA_SITE_KEY

let reCaptchaInstance: ReCaptchaInstance | null = null

const initRecaptcha = async (): Promise<void> => {
  try {
    reCaptchaInstance = await load(GOOGLE_RECAPTCHA_SITE_KEY, {
      autoHideBadge: true,
      useRecaptchaNet: true, // performance optimization that makes your reCAPTCHA load faster and more reliably from Google's dedicated reCAPTCHA CDN
    })
  } catch (error) {
    console.error(`Failed to load reCAPTCHA: ${error}`)
    throw error
  }
}

export const useCaptcha = () => {
  initRecaptcha()

  const getToken = async (action: RecaptchaAction): Promise<string> => {
    if (!reCaptchaInstance) throw new Error('reCAPTCHA not initialized')

    try {
      return await reCaptchaInstance.execute(action)
    } catch (error) {
      console.error(
        `Error executing reCAPTCHA for action "${action}": ${error}`,
      )
      return ''
    }
  }

  return {
    getToken,
  }
}
